import React, { useEffect, useState } from "react";
import {
  makeStyles,
  Paper,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TableFooter,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Button,
  Grid,
  Card,
  CardContent
} from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { format, parseISO, isBefore, isSameMonth } from "date-fns";
import esLocale from "date-fns/locale/es";
import enLocale from "date-fns/locale/en-US";
import ptLocale from "date-fns/locale/pt";
import frLocale from "date-fns/locale/fr";
import arLocale from "date-fns/locale/ar-SA";
import { i18n } from "../../translate/i18n";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useContext } from "react";

const localesMap = {
  es: esLocale,
  en: enLocale,
  pt: ptLocale,
  "pt-BR": ptLocale,
  fr: frLocale,
  ar: arLocale
};


const capitalizeAction = (action) => { if (!action) return ""; return action.charAt(0).toUpperCase() + action.slice(1); };

const useStyles = makeStyles((theme) => ({
  tableHeader: {
    fontWeight: 'bold',
    backgroundColor: "#1e1e1e",
    color: "#fff",
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  scrollArea: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  headerBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  formControl: {
    minWidth: 200,
    marginRight: theme.spacing(2),
  },
  summaryCard: {
    marginBottom: theme.spacing(3),
    background: theme.palette.type === "dark" ? "#1e1e2d" : "#f5f5f5",
  },
  summaryValue: {
    fontWeight: "bold",
    fontSize: "1.5rem"
  },
  valPositive: {
    color: "#4caf50"
  },
  valNegative: {
    color: "#ff5252"
  }
}));

const AiUsageLogs = () => {
  const classes = useStyles();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [availableMonths, setAvailableMonths] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { user, socket } = useContext(AuthContext);
  

  useEffect(() => {
    if (socket && user) {
      const handleAiLog = (data) => {
        if (data.action === "create") {
          setLogs(prev => {
            const newLog = { ...data.log, creditsCharged: parseFloat(data.log.creditsCharged) || 0 };
            return [...prev, newLog].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
          });
        }
      };
      
      socket.on(`company-${user.companyId}-aiLog`, handleAiLog);
      return () => {
        socket.off(`company-${user.companyId}-aiLog`, handleAiLog);
      };
    }
  }, [socket, user]);

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/ai-usage-logs");
        // Sort chronologically ascending to calculate balances properly
        // [FIX] PostgreSQL DECIMAL columns come as strings — parse to float
        const sortedData = data
          .map(log => ({ ...log, creditsCharged: parseFloat(log.creditsCharged) || 0 }))
          .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        setLogs(sortedData);
        
        // Generar los 12 meses del año actual (o de un rango deseado) para que siempre aparezcan
        const currentYear = new Date().getFullYear();
        const generatedMonths = [];
        for (let i = 1; i <= 12; i++) {
          const monthStr = i.toString().padStart(2, '0');
          generatedMonths.push(`${currentYear}-${monthStr}`);
        }
        
        const sortedMonths = generatedMonths.sort((a, b) => (a < b ? 1 : -1));
        setAvailableMonths(sortedMonths);
        
        // Seleccionar por defecto el mes actual
        const currentMonth = format(new Date(), "yyyy-MM");
        setSelectedMonth(currentMonth);
      } catch (err) {
        toastError(err);
      }
      setLoading(false);
    };
    fetchLogs();
  }, []);

  const filteredLogs = logs.filter(log => {
    if (selectedMonth === "all") return true;
    return format(parseISO(log.createdAt), "yyyy-MM") === selectedMonth;
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate summaries for the selected month
  let saldoInicial = 0;
  let totalRecargas = 0;
  let totalConsumos = 0;

  if (selectedMonth !== "all") {
    const monthDate = parseISO(`${selectedMonth}-01T00:00:00Z`);
    
    logs.forEach(log => {
      const logDate = parseISO(log.createdAt);
      const isRecharge = log.creditsCharged < 0;
      const amount = Math.abs(log.creditsCharged);

      // Si es antes del mes seleccionado, sumar al saldo inicial
      if (format(logDate, "yyyy-MM") < selectedMonth) {
        if (isRecharge) saldoInicial += amount;
        else saldoInicial -= amount;
      }
      // Si es del mes seleccionado, sumar a consumos/recargas
      else if (format(logDate, "yyyy-MM") === selectedMonth) {
        if (isRecharge) totalRecargas += amount;
        else totalConsumos += amount;
      }
    });
  } else {
    // Si es "Todos", el inicial es 0 y sumamos todo
    logs.forEach(log => {
      const isRecharge = log.creditsCharged < 0;
      const amount = Math.abs(log.creditsCharged);
      if (isRecharge) totalRecargas += amount;
      else totalConsumos += amount;
    });
  }

  const saldoFinal = saldoInicial + totalRecargas - totalConsumos;

  const handleExportExcel = () => {
    const currentLocale = localesMap[i18n.language] || esLocale;
    const monthName = selectedMonth !== "all" 
      ? format(parseISO(`${selectedMonth}-15T12:00:00`), "MMMM yyyy", { locale: currentLocale }).toUpperCase()
      : i18n.t("finance.aiUsage.allMonths").toUpperCase();

    const dateStr = selectedMonth !== "all" 
      ? format(parseISO(`${selectedMonth}-01T00:00:00Z`), "dd/MM/yyyy") 
      : "";

    // Estilos inline robustos para Excel
    const borderStyle = "border: 1px solid black;";
    const numStyle = "mso-number-format:'\\#\\,\\#\\#0\\.000_\\ '; text-align: right;";
    const titleStyle = "color: #0070C0; font-weight: bold; text-align: center; font-size: 12pt; border: none;";
    const headerStyle = "font-weight: bold; background-color: #f2f2f2; text-align: center;";
    
    // Construir tabla HTML con estilos inline directos
    let html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
    <head>
    <meta charset="UTF-8">
    <!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Estado de Cuenta</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
    <style>
      table { border-collapse: collapse; font-family: Calibri, sans-serif; font-size: 11pt; }
    </style>
    </head>
    <body>
      <table>
        <tr>
          <td style="${titleStyle}" colspan="5">CONSUMO IA MES DE ${monthName}</td>
        </tr>
        <tr>
          <td style="${borderStyle} ${headerStyle}">Fecha</td>
          <td style="${borderStyle} ${headerStyle}">Acción</td>
          <td style="${borderStyle} ${headerStyle}">Modelo</td>
          <td style="${borderStyle} ${headerStyle}">Consumos</td>
          <td style="${borderStyle} ${headerStyle}">Recargas</td>
        </tr>
    `;

    if (selectedMonth !== "all") {
      html += `
        <tr>
          <td style="${borderStyle}">${dateStr}</td>
          <td style="${borderStyle}">SALDO INICIAL</td>
          <td style="${borderStyle}"></td>
          <td style="${borderStyle}"></td>
          <td style="${borderStyle} ${numStyle}">${saldoInicial.toFixed(6)}</td>
        </tr>
      `;
    }

    const chronologicalLogs = [...filteredLogs];
    chronologicalLogs.forEach(log => {
      const isRecharge = log.creditsCharged < 0;
      const amount = Math.abs(log.creditsCharged);
      html += `
        <tr>
          <td style="${borderStyle}">${format(parseISO(log.createdAt), "dd/MM/yyyy HH:mm")}</td>
          <td style="${borderStyle}">${capitalizeAction(log.actionType)}</td>
          <td style="${borderStyle}">${log.modelUsed}</td>
          <td style="${borderStyle} ${!isRecharge ? numStyle : ''}">${!isRecharge ? amount.toFixed(6) : ''}</td>
          <td style="${borderStyle} ${isRecharge ? numStyle : ''}">${isRecharge ? amount.toFixed(6) : ''}</td>
        </tr>
      `;
    });

    html += `
        <tr>
          <td style="${borderStyle}"></td>
          <td style="${borderStyle} color: #00B050; font-weight: bold;">Ingresos</td>
          <td style="${borderStyle}"></td>
          <td style="${borderStyle}"></td>
          <td style="${borderStyle} color: #00B050; font-weight: bold; ${numStyle}">${totalRecargas.toFixed(6)}</td>
        </tr>
        <tr>
          <td style="${borderStyle}"></td>
          <td style="${borderStyle} color: #FF0000; font-weight: bold;">Egresos</td>
          <td style="${borderStyle}"></td>
          <td style="${borderStyle} color: #FF0000; font-weight: bold; ${numStyle}">${totalConsumos.toFixed(6)}</td>
          <td style="${borderStyle}"></td>
        </tr>
        <tr>
          <td style="${borderStyle}"></td>
          <td style="${borderStyle} color: #0070C0; font-weight: bold;">Saldo Final ${monthName}</td>
          <td style="${borderStyle}"></td>
          <td style="${borderStyle}"></td>
          <td style="${borderStyle} color: #0070C0; font-weight: bold; ${numStyle}">${saldoFinal.toFixed(6)}</td>
        </tr>
      </table>
    </body>
    </html>
    `;

    const blob = new Blob([html], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Estado_Cuenta_IA_${selectedMonth}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const currentLocale = localesMap[i18n.language] || esLocale;

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box className={classes.headerBox}>
        <Typography variant="h6">
          {i18n.t("finance.aiUsage.title")}
        </Typography>
        <Box display="flex" alignItems="center">
          <FormControl className={classes.formControl} size="small" variant="outlined">
            <InputLabel>{i18n.t("finance.aiUsage.billingMonth")}</InputLabel>
            <Select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              label={i18n.t("finance.aiUsage.billingMonth")}
            >
              <MenuItem value="all">{i18n.t("finance.aiUsage.allMonths")}</MenuItem>
              {availableMonths.map(month => (
                <MenuItem key={month} value={month}>
                  {format(parseISO(`${month}-15T12:00:00`), "MMMM yyyy", { locale: currentLocale }).toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="primary"
            startIcon={<GetAppIcon />}
            onClick={handleExportExcel}
            disabled={filteredLogs.length === 0}
          >
            {i18n.t("finance.aiUsage.exportExcel")}
          </Button>
        </Box>
      </Box>

      {/* Tarjetas de Resumen Financiero */}
      <Grid container spacing={3} className={classes.summaryCard}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={1}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {i18n.t("finance.aiUsage.initialBalance")}
              </Typography>
              <Typography className={classes.summaryValue}>
                {saldoInicial.toFixed(6)} USD
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={1}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {i18n.t("finance.aiUsage.incomes")}
              </Typography>
              <Typography className={`${classes.summaryValue} ${classes.valPositive}`}>
                {totalRecargas.toFixed(6)} USD
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={1}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {i18n.t("finance.aiUsage.expenses")}
              </Typography>
              <Typography className={`${classes.summaryValue} ${classes.valNegative}`}>
                {totalConsumos.toFixed(6)} USD
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={1}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {i18n.t("finance.aiUsage.finalBalance")}
              </Typography>
              <Typography className={`${classes.summaryValue} ${saldoFinal >= 0 ? classes.valPositive : classes.valNegative}`}>
                {saldoFinal.toFixed(6)} USD
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer className={classes.scrollArea}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeader} align="center">{i18n.t("finance.aiUsage.date")}</TableCell>
              <TableCell className={classes.tableHeader} align="center">{i18n.t("finance.aiUsage.action")}</TableCell>
              <TableCell className={classes.tableHeader} align="center">{i18n.t("finance.aiUsage.model")}</TableCell>
              <TableCell className={classes.tableHeader} align="right" style={{ paddingRight: "20px" }}>{i18n.t("finance.aiUsage.consumptions")}</TableCell>
              <TableCell className={classes.tableHeader} align="right" style={{ paddingRight: "20px" }}>{i18n.t("finance.aiUsage.recharges")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={5} />
            ) : (
              <>
                {/* Primera fila: Saldo Inicial */}
                {selectedMonth !== "all" && (
                  <TableRow style={{ backgroundColor: "rgba(0, 0, 0, 0.02)" }}>
                    <TableCell align="center">
                      {format(parseISO(`${selectedMonth}-01T00:00:00Z`), "dd/MM/yyyy")}
                    </TableCell>
                    <TableCell align="center" style={{ fontWeight: "bold" }}>{i18n.t("finance.aiUsage.initialBalanceRow")}</TableCell>
                    <TableCell align="center">-</TableCell>
                    <TableCell align="right" style={{ paddingRight: "20px" }}>-</TableCell>
                    <TableCell align="right" style={{ paddingRight: "20px", fontWeight: "bold", color: "#4caf50" }}>
                      {saldoInicial.toFixed(6)}
                    </TableCell>
                  </TableRow>
                )}

                {/* Mostrar los logs filtrados en orden cronológico (más antiguo arriba) */}
                {[...filteredLogs]
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((log) => (
                  <TableRow key={log.id}>
                    <TableCell align="center">
                      {format(parseISO(log.createdAt), "dd/MM/yyyy HH:mm")}
                    </TableCell>
                    <TableCell align="center">{capitalizeAction(log.actionType)}</TableCell>
                    <TableCell align="center">{log.modelUsed}</TableCell>
                    
                    {/* Consumos (Positivo en Rojo) alineado a la derecha con padding */}
                    <TableCell align="right" style={{ paddingRight: "20px" }}>
                      {log.creditsCharged > 0 ? (
                        <Typography style={{ color: "#ff5252", fontWeight: "bold", fontFamily: "monospace", fontSize: "14px" }}>
                          {log.creditsCharged.toFixed(7)}
                        </Typography>
                      ) : "-"}
                    </TableCell>
                    
                    {/* Recargas (Positivo en Verde) alineado a la derecha con padding */}
                    <TableCell align="right" style={{ paddingRight: "20px" }}>
                      {log.creditsCharged < 0 ? (
                        <Typography style={{ color: "#4caf50", fontWeight: "bold", fontFamily: "monospace", fontSize: "14px" }}>
                          {Math.abs(log.creditsCharged).toFixed(7)}
                        </Typography>
                      ) : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
            {!loading && filteredLogs.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {i18n.t("finance.aiUsage.noRecords")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={filteredLogs.length}
        rowsPerPage={rowsPerPage}
        page={page}
        SelectProps={{
          inputProps: {
            "aria-label": "filas por página",
          },
          native: true,
        }}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
      />
    </Box>
  );
};

export default AiUsageLogs;
